import { useEffect, useId, useRef } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../../lib/cn'
import { useT } from '../../lib/i18n'
import { useFocusTrap, useScrollLock } from '../../hooks/useFocusTrap'
import { IconButton } from './IconButton'
import { Icon } from './Icon'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

const SIZES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  /** A workbench-style tool (an editor, not a decision) — wide enough for a persistent side panel. */
  full: 'max-w-[1360px]',
}

export interface ModalProps {
  open: boolean
  onClose: () => void
  /** Sentence case, names what the dialog does. */
  title: string
  description?: ReactNode
  size?: ModalSize
  /** Replaces the default title/description block entirely — the close button stays Modal's own. */
  customHeader?: ReactNode
  /** Actions, right-aligned. The primary one repeats the title's verb. */
  footer?: ReactNode
  /** Default `justify-end`; a footer with its own leading content (a summary line) wants `justify-between`. */
  footerClassName?: string
  /** Off for destructive confirmations, so the choice is deliberate. */
  dismissOnOverlay?: boolean
  children: ReactNode
  className?: string
}

/** A focused decision on top of the workspace. Full-height sheet under 640 px. */
export const Modal = ({
  open,
  onClose,
  title,
  description,
  size = 'md',
  customHeader,
  footer,
  footerClassName,
  dismissOnOverlay = true,
  children,
  className,
}: ModalProps): ReactElement | null => {
  const t = useT()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const raw = useId()
  const titleId = `modal-${raw.replace(/:/g, '')}-title`
  const descriptionId = description ? `${titleId}-description` : undefined

  useFocusTrap(panelRef, open)
  useScrollLock(open)

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-60 flex items-end justify-center p-0 sm:items-center sm:p-4">
      <div
        className="anim-fade absolute inset-0 bg-chrome-900/55"
        onClick={dismissOnOverlay ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        className={cn(
          'anim-rise relative flex max-h-[92dvh] w-full flex-col rounded-t-lg border border-hair',
          'bg-surface shadow-overlay outline-none sm:rounded-lg',
          SIZES[size],
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-hair px-5 py-4">
          {customHeader ? (
            <>
              {/* `aria-labelledby` still points at this id — kept for assistive tech even though
                  sighted users see `customHeader`'s own richer title instead. */}
              <h2 id={titleId} className="sr-only">
                {title}
              </h2>
              {customHeader}
            </>
          ) : (
            <div className="min-w-0">
              <h2 id={titleId} className="t-h2 text-ink">
                {title}
              </h2>
              {description ? (
                <p id={descriptionId} className="t-small mt-1 text-ink-mute">
                  {description}
                </p>
              ) : null}
            </div>
          )}
          <IconButton
            label={t('common.actions.close')}
            icon={<Icon name="close" size={16} />}
            size="sm"
            onClick={onClose}
            tooltip={false}
          />
        </div>
        <div className="scroll-paper min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className={cn('flex flex-wrap items-center justify-end gap-2 border-t border-hair bg-paper px-5 py-3', footerClassName)}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
